import React, { useEffect, useRef, useState } from 'react';
import { ImperativePanelHandle, Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import DocPreviewShell from './components/DocPreviewShell';
import UnsupportedNotice from './components/UnsupportedNotice';
import RendererStatus from './components/RendererStatus';
import { useMediaQuery } from './hooks/useMediaQuery';
import { PreviewFile } from './types';
import { LoadError } from './utils/errorClassification';
import './DocPreviewDock.css';

interface DocPreviewDockProps {
  previewFile: PreviewFile | null;
  isResolving: boolean;
  resolveError: LoadError | null;
  onClose: () => void;
  onRefreshLink: () => void;
  children: React.ReactNode;
}

const NARROW_QUERY = '(max-width: 1024px)';
// Target width the preview opens to. Passed as expand()'s floor rather than
// hardcoded every time — react-resizable-panels restores whatever the user
// last manually dragged to if it's wider than this, only falling back to it
// on the very first open (see the library's "size before collapse" note).
const PREVIEW_OPEN_SIZE = 45;
// How long the open/close width animation takes — must match the CSS
// transition duration below, since this is also how long the animating
// class stays applied (removed after so live drag-resize afterward is never
// slowed by a transition on the same property it's dragging).
const SPLIT_ANIMATION_MS = 240;

// Docks the file preview beside the real chat UI, the same role Claude's
// artifact panel plays. `children` is the actual chat content (unchanged) —
// this only ever adds/removes the SECOND panel around it; the first panel
// holding `children` stays mounted in the same position whether the preview
// is open or not, so opening/closing it never remounts (and loses scroll
// position, in-progress edits, etc. in) the chat itself.
const DocPreviewDock: React.FC<DocPreviewDockProps> = ({ previewFile, isResolving, resolveError, onClose, onRefreshLink, children }) => {
  const isOpen = isResolving || !!resolveError || !!previewFile;
  const isNarrow = useMediaQuery(NARROW_QUERY);

  const previewPanelRef = useRef<ImperativePanelHandle>(null);
  const hasHandledFirstStateRef = useRef(false);
  const [isAnimatingSplit, setIsAnimatingSplit] = useState(false);

  // The preview Panel is now ALWAYS mounted (collapsed to 0 width when
  // closed, via collapsible/collapsedSize below) instead of being added and
  // removed from the tree — that's what makes an open/close width
  // transition possible at all: a freshly-mounted DOM node has no "before"
  // frame to animate from, but a width CHANGE on an already-mounted one does.
  useEffect(() => {
    const panel = previewPanelRef.current;
    if (!panel) return;

    if (!hasHandledFirstStateRef.current) {
      // First mount only: correct the panel's state to match isOpen WITHOUT
      // animating. autoSaveId persists the split ratio across sessions, so a
      // page reload could otherwise restore an old "open" width from
      // localStorage for a split-second before this even runs, or animate
      // shut on a reload that should just start closed silently.
      if (isOpen) {
        panel.expand(PREVIEW_OPEN_SIZE);
      } else {
        panel.collapse();
      }
      hasHandledFirstStateRef.current = true;
      return;
    }

    setIsAnimatingSplit(true);
    if (isOpen) {
      panel.expand(PREVIEW_OPEN_SIZE);
    } else {
      panel.collapse();
    }
    const timer = setTimeout(() => setIsAnimatingSplit(false), SPLIT_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const previewPane = (
    <>
      {isResolving && <RendererStatus label="Opening file…" />}
      {resolveError && (
        <UnsupportedNotice
          icon={resolveError.kind === 'cors' ? 'fas fa-ban' : 'fas fa-triangle-exclamation'}
          title="Couldn't open this file"
          description={resolveError.message}
          onRetry={onRefreshLink}
        />
      )}
      {previewFile && <DocPreviewShell doc={previewFile} onClose={onClose} onRefreshLink={onRefreshLink} />}
    </>
  );

  // Below the split's minimum usable width, a resizable side-by-side layout
  // doesn't work — the preview covers the screen instead, using the same
  // "opaque absolute overlay on top of unchanged content" approach FocusPanel
  // and FileUploadPanel already use elsewhere in Chat.tsx. children stays
  // mounted underneath the whole time, simply covered rather than hidden —
  // no need to toggle its display and risk losing scroll position or state.
  if (isNarrow) {
    return (
      <div className="doc-preview-dock-overlay-container">
        {children}
        {isOpen && <div className="doc-preview-dock-overlay doc-preview-dock-overlay--entering">{previewPane}</div>}
      </div>
    );
  }

  return (
    <PanelGroup
      direction="horizontal"
      autoSaveId="chat-doc-preview-split"
      className={`doc-preview-dock${isAnimatingSplit ? ' doc-preview-dock--animating' : ''}`}
    >
      <Panel defaultSize={100} minSize={30}>
        {children}
      </Panel>
      <PanelResizeHandle
        className={`doc-preview-resize-handle${isOpen ? '' : ' doc-preview-resize-handle--hidden'}`}
      />
      <Panel
        ref={previewPanelRef}
        defaultSize={0}
        minSize={25}
        collapsible
        collapsedSize={0}
      >
        {previewPane}
      </Panel>
    </PanelGroup>
  );
};

export default DocPreviewDock;
